import React, { PropsWithChildren, useCallback, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import { HomePage } from "./components/Home/HomePage";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SavusLogo from "./components/util/SavusLogo";
import classNames from "classnames";
import { useAppState } from "./state/State";
import DocumentationDetails from "./components/Documentation/DocumentationDetails";
import LogoWhite from "./components/util/LogoWhite";

// Lazy-loaded components
const Questionnaire = lazy(() =>
  import("./components/Questionaire/Questionnaire").then(module => ({ default: module.Questionnaire })),
);
const Documentation = lazy(() =>
  import("./components/Documentation/Documentation").then(module => ({ default: module.Documentation })),
);
const DocumentationQuestionnaireDetails = lazy(() =>
  import("./components/Documentation/DocumentationQuestionnaireDetails").then(module => ({
    default: module.DocumentationQuestionnaireDetails,
  })),
);
const DocumentationCTX = lazy(() =>
  import("./components/Documentation/DocumentationContext").then(module => ({ default: module.DocumentationCTX })),
);
const DocumentationAuthorization = lazy(() =>
  import("./components/Documentation/DocumentationAuthorization").then(module => ({
    default: module.DocumentationAuthorization,
  })),
);
const DocumentationSDK = lazy(() =>
  import("./components/Documentation/DocumentationSDK").then(module => ({ default: module.DocumentationSDK })),
);
const QuoteManagement = lazy(() =>
  import("./components/QuoteManagement/QuoteManagement").then(module => ({ default: module.QuoteManagement })),
);
const BusinessReports = lazy(() =>
  import("./components/BusinessReports/BusinessReports").then(module => ({ default: module.BusinessReports })),
);
const BusinessReportsStateProvider = lazy(() =>
  import("./state/BusinessReportsState").then(module => ({ default: module.BusinessReportsStateProvider })),
);
const QuestionnaireManagement = lazy(() =>
  import("./components/QuestionnaireManagement/QuestionnaireManagement").then(module => ({
    default: module.QuestionnaireManagement,
  })),
);
const UtilsPage = lazy(() => import("./components/Utils/UtilsPage").then(module => ({ default: module.UtilsPage })));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className={"h-screen w-screen flex justify-center items-center"}>
    <div className={"max-w-[720px] m-4 rounded border bg-white p-6 flex flex-col gap-10 items-center"}>Loading...</div>
  </div>
);

const AuthCheck = ({ children, auth }: PropsWithChildren<{ auth: boolean }>) => {
  const token = localStorage.getItem("__token");
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getMe,
    state: {
      user: { loading, me },
    },
  } = useAppState();

  useEffect(() => {
    if (!token && auth && location.pathname !== "/") {
      navigate("/");
    } else if (token) {
      getMe(token);
    }
  }, [token, auth, location.pathname, navigate, getMe]);

  useEffect(() => {
    if (me?.ok && !auth) {
      navigate("/questionnaire");
    } else if (me?.ok === false && auth) {
      navigate("/");
    }
  }, [me, auth, navigate]);

  if (loading)
    return (
      <div className={"h-screen w-screen flex justify-center items-center"}>
        <div className={"max-w-[720px] m-4 rounded border bg-white p-6 flex flex-col gap-10 items-center"}>
          Gathering user data...
        </div>
      </div>
    );

  if (!token && auth)
    return (
      <div className={"h-screen w-screen flex justify-center items-center"}>
        <div className={"max-w-[720px] m-4 rounded border bg-white p-6 flex flex-col gap-10 items-center"}>
          Redirecting to login page...
        </div>
      </div>
    );

  if (token && me?.ok && !auth)  
    return (
      <div className={"h-screen w-screen flex justify-center items-center"}>
        <div className={"max-w-[720px] m-4 rounded border bg-white p-6 flex flex-col gap-10 items-center"}>
          Redirecting to questionnaire page...
        </div>
      </div>
    );

  return <div className={"relative"}>{children}</div>;
};

const AppHeader = ({ auth }: PropsWithChildren<{ auth: boolean }>) => {
  const logout = useCallback(() => {
    localStorage.removeItem("__token");
    window.location.reload();
  }, []);
  return (
    <div className='relative w-full h-16 bg-white flex justify-between items-center border-gray-200 border-b-[3px] p-4 z-[9] '>
      <SavusLogo />

      {auth && (
        <div className={"flex gap-2 items-start"}>
          <a
            href='/questionnaire'
            className={classNames(
              "rounded px-2 py-0.5 whitespace-nowrap max-w-32 text-sm text-ellipsis overflow-hidden border-gray-200 border hover:bg-primary hover:text-white transition-all cursor-pointer",
              {
                "bg-primary text-white":
                  window.location.href.includes("/questionnaire") &&
                  !window.location.href.includes("/questionnaire-table"),
              },
            )}
          >
            Questionnaires
          </a>
          <a
            href='/documentation'
            className={classNames(
              "rounded px-2 py-0.5 whitespace-nowrap max-w-32 text-sm text-ellipsis overflow-hidden border-gray-200 border hover:bg-primary hover:text-white transition-all cursor-pointer",
              {
                "bg-primary text-white": window.location.href.includes("/documentation"),
              },
            )}
          >
            Documentation
          </a>
          {/*<a*/}
          {/*  href='/quotes'*/}
          {/*  className={classNames(*/}
          {/*    "rounded px-2 py-0.5 whitespace-nowrap max-w-32 text-sm text-ellipsis overflow-hidden border-gray-200 border hover:bg-primary hover:text-white transition-all cursor-pointer",*/}
          {/*    {*/}
          {/*      "bg-primary text-white": window.location.href.includes("/quotes"),*/}
          {/*    },*/}
          {/*  )}*/}
          {/*>*/}
          {/*  Quote Runner AI*/}
          {/*</a>*/}
          <a
            href='/reports'
            className={classNames(
              "rounded px-2 py-0.5 whitespace-nowrap max-w-32 text-sm text-ellipsis overflow-hidden border-gray-200 border hover:bg-primary hover:text-white transition-all cursor-pointer",
              {
                "bg-primary text-white": window.location.href.includes("/reports"),
              },
            )}
          >
            Business Reports
          </a>
          <a
            href='/utils'
            className={classNames(
              "rounded px-2 py-0.5 whitespace-nowrap max-w-32 text-sm text-ellipsis overflow-hidden border-gray-200 border hover:bg-primary hover:text-white transition-all cursor-pointer",
              {
                "bg-primary text-white": window.location.href.includes("/utils"),
              },
            )}
          >
            Tools
          </a>

          <button
            onClick={logout}
            className='rounded ml-4 text-sm bg-red-400 text-white px-4 py-0.5 border-gray-200 border hover:bg-white hover:text-red-400 hover:border-red-400 transition-all cursor-pointer'
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
};

function App({ auth }: PropsWithChildren<{ auth: boolean }>) {
  return (
    <div className={"w-screen h-screen bg-gray-50 overflow-y-auto px-1 flex flex-col gap-[1px] "} id={"app"}>
      <ToastContainer />
      <Router>
        <Routes>
          <Route
            path='/'
            element={
              <AuthCheck auth={false}>
                <AppHeader auth={auth} />
                <HomePage />
              </AuthCheck>
            }
          />
          <Route
            path='/documentation'
            element={
              <AuthCheck auth={true}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Documentation />
                </Suspense>
              </AuthCheck>
            }
          />
          <Route
            path='/documentation/view'
            element={
              <AuthCheck auth={true}>
                <div className='bg-primary text-white p-4 rounded-lg shadow-xl flex items-center gap-2'>
                  <div className='max-w-[200px] min-w-64 flex'>
                    <LogoWhite />
                  </div>
                  <div className='text-center italic'>
                    <h1 className='text-3xl font-light'>ConnectX</h1>
                  </div>
                </div>
                <Suspense fallback={<LoadingSpinner />}>
                  <DocumentationCTX>
                    <DocumentationDetails />
                  </DocumentationCTX>
                </Suspense>
              </AuthCheck>
            }
          />
          <Route
            path='/documentation/authorization'
            element={
              <AuthCheck auth={true}>
                <Suspense fallback={<LoadingSpinner />}>
                  <DocumentationAuthorization />
                </Suspense>
              </AuthCheck>
            }
          />
          <Route
            path='/documentation/sdk'
            element={
              <AuthCheck auth={true}>
                <Suspense fallback={<LoadingSpinner />}>
                  <DocumentationSDK />
                </Suspense>
              </AuthCheck>
            }
          />

          <Route
            path='/questionnaire'
            element={
              <AuthCheck auth>
                <AppHeader auth={auth} />
                <Suspense fallback={<LoadingSpinner />}>
                  <Questionnaire />
                </Suspense>
              </AuthCheck>
            }
          />

          <Route
            path='/quotes'
            element={
              <AuthCheck auth>
                <AppHeader auth={auth} />
                <Suspense fallback={<LoadingSpinner />}>
                  <QuoteManagement />
                </Suspense>
              </AuthCheck>
            }
          />
          <Route
            path='/reports'
            element={
              <AuthCheck auth>
                <AppHeader auth={auth} />
                <Suspense fallback={<LoadingSpinner />}>
                  <BusinessReportsStateProvider>
                    <BusinessReports />
                  </BusinessReportsStateProvider>
                </Suspense>
              </AuthCheck>
            }
          />
          <Route
            path='/questionnaire/:id'
            element={
              <AuthCheck auth>
                <AppHeader auth={auth} />
                <Suspense fallback={<LoadingSpinner />}>
                  <QuestionnaireManagement />
                </Suspense>
              </AuthCheck>
            }
          />
          <Route
            path='/utils'
            element={
              <AuthCheck auth>
                <AppHeader auth={auth} />
                <Suspense fallback={<LoadingSpinner />}>
                  <UtilsPage />
                </Suspense>
              </AuthCheck>
            }
          />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
